/** @jsx jsx */
import React from 'react';
import { partial } from 'lodash';
import { css, jsx } from '@emotion/core';
import {
  ApiKeys,
  PaymentSourceInput,
  PayPalPaymentSource,
  SupportedPaymentMethod,
  PaymentSourceType,
} from '@zapier/payment/src/types';
import { Button, ButtonGroup, Link, Text } from '@zapier/design-system';
import PaymentMethodImage from '@zapier/payment/src/components/PaymentMethodImage';
import PaypalButton from '@zapier/payment/src/components/PaypalButton';
import PaypalInfo from '@zapier/payment/src/components/PaypalInfo';
import { useSize } from '@zapier/payment/src/hooks/useSize';
import PaymentMethod from '../PaymentMethod';
import PaymentMethodSelect from '../PaymentMethodSelect';

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys;
  /** Currently select method */
  currentMethod: PaymentSourceType | null;
  /** Hide switch component button */
  hideSwitch: boolean;
  /** Show editor */
  isEditing: boolean;
  /** Call to canel edit */
  onCancelEdit: () => void;
  /** Call to start edit */
  onEdit: () => void;
  /** Call when editor throws an error */
  onErrorEdit: (error: Error, method?: PaymentSourceType) => void;
  /** Call to save current edit */
  onSaveEdit: (paypalDetails: PaymentSourceInput) => void;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType) => void;
  /** User's currently saved paypal details */
  savedSource: PayPalPaymentSource | null;
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>;
};

const buttonGroupWrapper = css`
  margin-top: 15px;
  margin-bottom: 15px;
`;

const contentCss = css`
  text-align: center;
`;

const labelCss = css`
  margin-right: 20px;
`;

const renderLabel = (props: Props) =>
  props.savedSource && !props.isEditing ? (
    <PaypalInfo paypal={props?.savedSource?.details} />
  ) : (
    <React.Fragment>
      <PaymentMethodImage imageType={PaymentSourceType.paypal} isSmall={true} />
      <span css={labelCss}>
        <Text tag="h3" type="subtitle700">
          Connect PayPal
        </Text>
      </span>
    </React.Fragment>
  );

const renderAside = (props: Props, isSmall: boolean) => {
  if (!props.isEditing) {
    return (
      <Button
        ariaLabel="Edit Payment Method"
        color="greyAlt"
        onClick={props.onEdit}
      >
        {props.savedSource ? `Edit${!isSmall ? ' Payment Method' : ''}` : 'Add'}
      </Button>
    );
  } else {
    return props.hideSwitch ? null : (
      <PaymentMethodSelect
        currentMethod={props.currentMethod}
        onChange={props.onSelectSource}
        supportedMethods={props.supportedMethods}
      />
    );
  }
};

const PaymentMethodPaypal: React.FC<Props> = props => {
  const size = useSize();
  const isSmall = size === 'small';
  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={partial(renderAside, props, isSmall)}
      renderLabel={partial(renderLabel, props)}
    >
      <div css={contentCss}>
        <Text type="bodyText800">
          {props.savedSource
            ? 'You can reconnect to use a different PayPal account.'
            : 'Please connect your PayPal account.'}
        </Text>
        <div css={buttonGroupWrapper}>
          <ButtonGroup
            columnSize={isSmall ? '100%' : 'minmax(100px, 250px)'}
            justify="center"
          >
            <Button
              ariaLabel="Cancel"
              color="greyAlt"
              isFullWidth={true}
              onClick={props.onCancelEdit}
            >
              Cancel
            </Button>
            <PaypalButton
              apiKey={props.apiKeys.braintree}
              onAuthorize={props.onSaveEdit}
              onError={props.onErrorEdit}
            />
          </ButtonGroup>
        </div>
        <Link
          href="https://www.paypal.com/cgi-bin/webscr?cmd=xpt/Marketing/popup/OLCWhatIsPayPal-outside"
          target="_blank"
        >
          What is PayPal?
        </Link>
      </div>
    </PaymentMethod>
  );
};

export default PaymentMethodPaypal;
